import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import Http from "../../../Common/Http";

const STATUS_OPTIONS = [
  { label: "To Do", value: "To Do" },
  { label: "In Progress", value: "In Progress" },
  { label: "Review", value: "Review" },
  { label: "Done", value: "Done" },
];

// start_date / end_date are optional — tasks may be imported unscheduled.
const REQUIRED_HEADERS = ["title", "status"];

// Normalize a date cell from CSV/Excel into YYYY-MM-DD.
// Accepts: "YYYY-MM-DD", "DD/MM/YYYY", or an Excel serial number.
const normalizeDate = (val) => {
  if (val === undefined || val === null || val === "") return "";
  if (typeof val === "number") {
    const d = XLSX.SSF.parse_date_code(val);
    if (!d) return "";
    const mm = String(d.m).padStart(2, "0");
    const dd = String(d.d).padStart(2, "0");
    return `${d.y}-${mm}-${dd}`;
  }
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }
  const parsed = new Date(s);
  if (!isNaN(parsed)) return parsed.toISOString().slice(0, 10);
  return "";
};

// Case-insensitive name lookup. Returns the id of the first match, or null.
const findIdByName = (list, name, labelKey = "label") => {
  if (!name) return null;
  const needle = String(name).trim().toLowerCase();
  if (!needle) return null;
  const hit = list.find((item) => String(item[labelKey] || "").trim().toLowerCase() === needle);
  return hit ? hit.value : null;
};

const BulkUpload = ({ onSuccess, setVisible }) => {
  const toast = useRef(null);
  const fileInputRef = useRef(null);

  const [loadingRefs, setLoadingRefs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]); // parsed + resolved rows
  const [fileName, setFileName] = useState("");

  const [projects, setProjects] = useState([]);
  const [stories, setStories] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [labels, setLabels] = useState([]);
  const [developers, setDevelopers] = useState([]);

  // Load all reference lists in parallel on mount.
  useEffect(() => {
    let cancelled = false;

    const loadRefs = async () => {
      try {
        const [projectsRes, storiesRes, taskTypesRes, labelsRes, developersRes] = await Promise.all([
          Http.get("/project"),
          Http.get("/story"),
          Http.get("/task-type"),
          Http.get("/label"),
          Http.get("/staff-department-wise/30"),
        ]);

        if (cancelled) return;

        setProjects(
          (projectsRes.data?.data || []).map((p) => ({ label: p.title, value: p.id }))
        );
        setStories(
          (storiesRes.data?.data || []).map((s) => ({
            label: s.title,
            value: s.id,
            project_id: s.project_id,
          }))
        );
        setTaskTypes(
          (taskTypesRes.data?.data || []).map((t) => ({ label: t.task_type, value: t.id }))
        );
        setLabels(
          (labelsRes.data?.data || []).map((l) => ({ label: l.name, value: l.id }))
        );
        const devs = Array.isArray(developersRes.data) ? developersRes.data : [];
        setDevelopers(
          devs.map((d) => ({
            label: d.name || `${d.first_name || ""} ${d.last_name || ""}`.trim() || `ID: ${d.id}`,
            value: d.id,
          }))
        );
      } catch (err) {
        console.error("Error loading reference data:", err);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Could not load reference data (projects/stories/etc.).",
          life: 4000,
        });
      } finally {
        if (!cancelled) setLoadingRefs(false);
      }
    };

    loadRefs();
    return () => {
      cancelled = true;
    };
  }, []);

  // Validate a single resolved row. Returns an object map of fieldName -> error message.
  const validateRow = (row) => {
    const errors = {};
    if (!row.title || !String(row.title).trim()) errors.title = "Required";
    if (!row.status) errors.status = "Required";
    // Planned dates are optional. Only enforce ordering / start-with-end.
    if (row.end_date && !row.start_date) {
      errors.start_date = "Required with end date";
    }
    if (row.start_date && row.end_date && row.end_date < row.start_date) {
      errors.end_date = "End before start";
    }
    if (!row.project_id && !row.story_id) {
      errors.project_id = "Project or story required";
    }
    return errors;
  };

  // Run after refs load — re-resolve names → IDs for any rows already parsed.
  const resolveRow = (raw) => {
    const projectId = findIdByName(projects, raw.project);
    const storyId = findIdByName(stories, raw.story);
    const taskTypeId = findIdByName(taskTypes, raw.task_type);
    const labelId = findIdByName(labels, raw.label);

    const devNames = String(raw.developers || "")
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);
    const developerIds = devNames
      .map((n) => findIdByName(developers, n))
      .filter((id) => id != null);

    // If story is set but project isn't, derive project from the story.
    let finalProjectId = projectId;
    if (!finalProjectId && storyId) {
      const story = stories.find((s) => s.value === storyId);
      if (story) finalProjectId = story.project_id;
    }

    return {
      title: raw.title || "",
      description: raw.description || "",
      project_id: finalProjectId,
      story_id: storyId,
      task_type_id: taskTypeId,
      label_id: labelId,
      developer_ids: developerIds,
      points: raw.points === "" || raw.points == null ? null : Number(raw.points) || null,
      status: raw.status || "To Do",
      start_date: normalizeDate(raw.start_date),
      end_date: normalizeDate(raw.end_date),
      estimated_time: raw.estimated_time || "",
    };
  };

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });

        // Normalize header keys to lowercase snake.
        const cleaned = json.map((r) => {
          const out = {};
          Object.keys(r).forEach((k) => {
            const key = String(k).trim().toLowerCase().replace(/\s+/g, "_");
            out[key] = r[k];
          });
          return out;
        });

        if (cleaned.length === 0) {
          toast.current?.show({
            severity: "warn",
            summary: "Empty file",
            detail: "No rows found in the uploaded file.",
            life: 3000,
          });
          return;
        }

        const headers = Object.keys(cleaned[0]);
        const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
        if (missing.length) {
          toast.current?.show({
            severity: "error",
            summary: "Missing columns",
            detail: `Required columns: ${missing.join(", ")}`,
            life: 5000,
          });
          return;
        }

        const resolved = cleaned.map(resolveRow);
        setRows(resolved);
      } catch (err) {
        console.error("Error parsing file:", err);
        toast.current?.show({
          severity: "error",
          summary: "Parse error",
          detail: "Could not read the CSV file. Make sure it's a valid CSV.",
          life: 4000,
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  // Filtered story list per row, scoped to the selected project (if any).
  const storyOptionsFor = (projectId) =>
    projectId ? stories.filter((s) => s.project_id === projectId) : stories;

  const updateCell = (rowIndex, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const updated = { ...next[rowIndex], [field]: value };

      // Cascade: changing project clears story if it doesn't belong to the new project.
      if (field === "project_id") {
        const story = stories.find((s) => s.value === updated.story_id);
        if (story && story.project_id !== value) {
          updated.story_id = null;
        }
      }
      // Cascade: picking a story auto-fills project.
      if (field === "story_id" && value) {
        const story = stories.find((s) => s.value === value);
        if (story) updated.project_id = story.project_id;
      }

      next[rowIndex] = updated;
      return next;
    });
  };

  const removeRow = (rowIndex) => {
    setRows((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  // Per-row error map memoized so cells can highlight without recomputing on every keystroke unnecessarily.
  const rowErrors = useMemo(() => rows.map(validateRow), [rows]);
  const totalErrors = rowErrors.reduce((acc, e) => acc + Object.keys(e).length, 0);

  const handleSave = async () => {
    if (totalErrors > 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Fix errors",
        detail: `${totalErrors} validation error(s) remain. Please fix the highlighted cells.`,
        life: 4000,
      });
      return;
    }
    if (rows.length === 0) return;

    setSaving(true);
    try {
      const res = await Http.post("/task/bulk-store", { tasks: rows });
      const created = res.data?.data || [];
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: `${created.length} task(s) created`,
        life: 3000,
      });
      if (onSuccess) onSuccess(created);
      if (setVisible) setVisible(false);
    } catch (err) {
      const data = err.response?.data || {};
      // `data.error` is the raw technical cause (e.g. a SQL message). It is for
      // developers only — log it to the console, never show it to the user.
      console.error("Bulk save failed:", data.error || err);

      const apiMsg = data.message || "Failed to save tasks. Please try again.";

      // Backend returns `errors` as a list of human-readable, row-numbered
      // strings (422). Build a multi-line detail so the user can see exactly
      // which rows to fix.
      const apiErrors = Array.isArray(data.errors)
        ? data.errors
        : data.errors
        ? Object.values(data.errors).flat()
        : [];

      const detail = apiErrors.length ? (
        <div>
          <div className="mb-1">{apiMsg}</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {apiErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      ) : (
        apiMsg
      );

      toast.current?.show({ severity: "error", summary: "Error", detail, life: 10000 });
    } finally {
      setSaving(false);
    }
  };

  // ----- Cell renderers -----
  const errClass = (rowIndex, field) => (rowErrors[rowIndex]?.[field] ? "p-invalid" : "");
  const errTitle = (rowIndex, field) => rowErrors[rowIndex]?.[field] || "";

  const cellInput = (rowIndex, field, width = 160) => (
    <InputText
      value={rows[rowIndex][field] || ""}
      onChange={(e) => updateCell(rowIndex, field, e.target.value)}
      className={errClass(rowIndex, field)}
      title={errTitle(rowIndex, field)}
      style={{ width }}
    />
  );

  const cellDropdown = (rowIndex, field, options, width = 170, opts = {}) => (
    <Dropdown
      value={rows[rowIndex][field] ?? null}
      options={options}
      onChange={(e) => updateCell(rowIndex, field, e.value)}
      placeholder={opts.placeholder || "Select"}
      filter={opts.filter !== false}
      showClear={opts.showClear !== false}
      className={errClass(rowIndex, field)}
      title={errTitle(rowIndex, field)}
      style={{ width }}
      scrollHeight="220px"
    />
  );

  return (
    <div>
      <Toast ref={toast} />

      {loadingRefs ? (
        <div className="text-center p-4">
          <ProgressSpinner style={{ width: 40, height: 40 }} />
          <p className="mt-2 mb-0">Loading reference data…</p>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <input
                type="file"
                accept=".csv,text/csv"
                ref={fileInputRef}
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="form-control"
                style={{ maxWidth: 320 }}
              />
              {fileName && <small className="text-muted">{fileName}</small>}
            </div>
            <a href="/task-bulk-template.csv" download className="btn btn-link p-0">
              <i className="fa fa-download me-1" /> Download sample CSV
            </a>
          </div>

          {rows.length > 0 && (
            <>
              <div className="mb-2 d-flex justify-content-between align-items-center">
                <div>
                  <strong>{rows.length}</strong> row(s) loaded
                  {totalErrors > 0 && (
                    <span className="text-danger ms-2">· {totalErrors} validation error(s)</span>
                  )}
                </div>
              </div>

              <DataTable
                value={rows}
                paginator={rows.length > 10}
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                responsiveLayout="scroll"
                scrollable
                scrollHeight="50vh"
                size="small"
                emptyMessage="No rows"
              >
                <Column
                  header="#"
                  body={(_, opts) => opts.rowIndex + 1}
                  style={{ width: 50 }}
                  frozen
                />
                <Column
                  header="Title *"
                  body={(_, opts) => cellInput(opts.rowIndex, "title", 200)}
                  style={{ minWidth: 220 }}
                />
                <Column
                  header="Project *"
                  body={(_, opts) =>
                    cellDropdown(opts.rowIndex, "project_id", projects, 180, {
                      placeholder: "Project",
                    })
                  }
                  style={{ minWidth: 200 }}
                />
                <Column
                  header="Story"
                  body={(_, opts) =>
                    cellDropdown(
                      opts.rowIndex,
                      "story_id",
                      storyOptionsFor(rows[opts.rowIndex].project_id),
                      180,
                      { placeholder: "Story" }
                    )
                  }
                  style={{ minWidth: 200 }}
                />
                <Column
                  header="Task Type"
                  body={(_, opts) =>
                    cellDropdown(opts.rowIndex, "task_type_id", taskTypes, 160, {
                      placeholder: "Type",
                    })
                  }
                  style={{ minWidth: 180 }}
                />
                <Column
                  header="Label"
                  body={(_, opts) =>
                    cellDropdown(opts.rowIndex, "label_id", labels, 150, {
                      placeholder: "Label",
                    })
                  }
                  style={{ minWidth: 170 }}
                />
                <Column
                  header="Developers"
                  body={(_, opts) => (
                    <MultiSelect
                      value={rows[opts.rowIndex].developer_ids || []}
                      options={developers}
                      onChange={(e) => updateCell(opts.rowIndex, "developer_ids", e.value)}
                      placeholder="Developers"
                      filter
                      display="chip"
                      style={{ width: 230 }}
                      scrollHeight="220px"
                    />
                  )}
                  style={{ minWidth: 250 }}
                />
                <Column
                  header="Points"
                  body={(_, opts) => (
                    <InputNumber
                      value={rows[opts.rowIndex].points}
                      onValueChange={(e) => updateCell(opts.rowIndex, "points", e.value)}
                      min={0}
                      inputStyle={{ width: 80 }}
                    />
                  )}
                  style={{ minWidth: 110 }}
                />
                <Column
                  header="Status *"
                  body={(_, opts) =>
                    cellDropdown(opts.rowIndex, "status", STATUS_OPTIONS, 140, {
                      filter: false,
                      showClear: false,
                      placeholder: "Status",
                    })
                  }
                  style={{ minWidth: 160 }}
                />
                <Column
                  header="Start *"
                  body={(_, opts) => (
                    <InputText
                      type="date"
                      value={rows[opts.rowIndex].start_date || ""}
                      onChange={(e) => updateCell(opts.rowIndex, "start_date", e.target.value)}
                      className={errClass(opts.rowIndex, "start_date")}
                      title={errTitle(opts.rowIndex, "start_date")}
                      style={{ width: 150 }}
                    />
                  )}
                  style={{ minWidth: 170 }}
                />
                <Column
                  header="End *"
                  body={(_, opts) => (
                    <InputText
                      type="date"
                      value={rows[opts.rowIndex].end_date || ""}
                      onChange={(e) => updateCell(opts.rowIndex, "end_date", e.target.value)}
                      className={errClass(opts.rowIndex, "end_date")}
                      title={errTitle(opts.rowIndex, "end_date")}
                      style={{ width: 150 }}
                    />
                  )}
                  style={{ minWidth: 170 }}
                />
                <Column
                  header="Est. Time"
                  body={(_, opts) => cellInput(opts.rowIndex, "estimated_time", 120)}
                  style={{ minWidth: 140 }}
                />
                <Column
                  header="Description"
                  body={(_, opts) => cellInput(opts.rowIndex, "description", 200)}
                  style={{ minWidth: 220 }}
                />
                <Column
                  header=""
                  body={(_, opts) => (
                    <Button
                      icon="pi pi-trash"
                      className="p-button-rounded p-button-danger p-button-text p-button-sm"
                      onClick={() => removeRow(opts.rowIndex)}
                      tooltip="Remove row"
                      tooltipOptions={{ position: "top" }}
                    />
                  )}
                  style={{ width: 60 }}
                  frozen
                  alignFrozen="right"
                />
              </DataTable>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  label="Cancel"
                  className="p-button-text"
                  onClick={() => setVisible && setVisible(false)}
                  disabled={saving}
                />
                <Button
                  label={saving ? "Saving…" : `Save ${rows.length} task(s)`}
                  icon="pi pi-check"
                  className="p-button-success"
                  onClick={handleSave}
                  disabled={saving || totalErrors > 0}
                />
              </div>
            </>
          )}

          {rows.length === 0 && !loadingRefs && (
            <div className="text-center text-muted p-4">
              Pick a CSV file above to preview rows.
              <br />
              <small>
                Required columns: <code>title, status</code>. Optional:{" "}
                <code>start_date, end_date, project, story, task_type, label, developers, points, estimated_time, description</code>.
              </small>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkUpload;
